import React from "react";
import type { FluentIcon } from "@fluentui/react-icons";
import DisplayTableRow from "./DisplayTableRow";
import DisplayText from "../text/DisplayText";
import { IRepertoireField } from "../../../repertoire/types/IRepertoireField";
import { EntityType } from "../../../repertoire/types/EntityType";
import TableHeaderSection from "../../../repertoire/components/tableHeaderSection/TableHeaderSection";
import { IInstrumentation } from "../../../repertoire/types/IInstrumentation";
import { IArtist } from "../../../repertoire/types/IArtist";
import { IContributorItem } from "../../../repertoire/types/IContributorItem";
import { ITitle } from "../../../repertoire/types/ITitle";
import { IProductWorkDataItems } from "../../../repertoire/types/IProductWorkDataItems";
import { ILookupInstance } from "../../../lookup/types/ILookup";
import { IUnpaidClaimsSearchResult } from "../../types/IUnpaidClaimsSearchResult";
import { IAgreementWorkDataItems } from "../../../repertoire/types/IAgreementWorkDataItems";
import { ADD_TO_PACKAGE_ACTION, CONTRIBUTORS_KEY, PACKAGE_LOCALIZATION_FIELDS, SEARCH_VIEW_REGISTERED_WORKS } from "../../../repertoire/Consts";
import PaginationView from "../../../repertoire/components/paginationView/PaginationView";
import { hasValue } from "../../../util/tools";
import { AddToPackagesHandler } from "../../../repertoire/types/AddToPackagesHandler";
import { ILicenseVenueSearchQuery } from "../../../repertoire/types/ILicenseVenueSearchQuery";
import { showYesNoPrompt, hideModal } from "../../../redux/reducers/ModalReducer";
import AddAllToPackageButton from "../button/AddAllToPackageButton";

export interface IDisplayTableProps {
    tableContents: IInstrumentation[] | IArtist[] | IContributorItem[] | ITitle[] | IProductWorkDataItems[] | IUnpaidClaimsSearchResult[] | any[];
    dataGridTableData: IRepertoireField[];
    showRowNumber?: boolean;
    isPaginationView?: boolean;
    isExpandable?: boolean;
    rowActions?: IRowActions;
    removeRowAction?: (item: IProductWorkDataItems | IInstrumentation | IArtist | IContributorItem | ITitle | IUnpaidClaimsSearchResult | IAgreementWorkDataItems) => void;
    lookupOptionsAndOnChangeFunctions?: ILookupOptionsAndOnChangeFunctions;
    isSearchResultTable?: boolean;
    sortTableByColumn?: (section: string, ascending:boolean) => void;
    activeSortHeaderSection?: string;
    isAscending?: boolean;
    isRenderLinkTitle?: boolean;
    getAgreementDetails?: (dataSource: string, agreementID) => void;
    addToPackages?: AddToPackagesHandler;
    addCopiedProductWorks?: (order: number) => void;
    addLicenseVenueToUsage?: (venueID: number) => void;
    showYesNoPrompt?: typeof showYesNoPrompt;
    hideModal?: typeof hideModal;
    packageActionsDisabled?: boolean;
    packageMessageData?: IRepertoireField[];
    deferredPackageSelection?: boolean;
}

export interface ILookupOptionsAndOnChangeFunctions {
    musicUsage?: {
        options: ILookupInstance[],
        onChangeValue: (
            value: any,
            fieldName: string,
            rowIndex: number,
        ) => void,
    },
    duration?: {
        onChangeValue: (
            value: any,
            fieldName: string,
            rowIndex: number,
        ) => void,
    },
    type?: {
        onChangeValue: (
            value: any,
            fieldName: string,
            rowIndex: number,
        ) => void,
    },
    exclusionUntil?: {
        options: ILookupInstance[],
        onChangeValue: (
            value: any,
            fieldName: string,
            rowIndex: number,
        ) => void,
    },
    worksRetainedUntil?: {
        options: ILookupInstance[],
        onChangeValue: (
            value: any,
            fieldName: string,
            rowIndex: number,
        ) => void,
    },
    workCollectionUntil?: {
        options: ILookupInstance[],
        onChangeValue: (
            value: any,
            fieldName: string,
            rowIndex: number,
        ) => void,
    },
    useDefaultCollection?: {
        options: ILookupInstance[],
        onChangeValue: (
            value: any,
            fieldName: string,
            rowIndex: number,
        ) => void,
    },
    excludedUntilDate?: {
        onChangeValue: (
            value: any,
            fieldName: string,
            rowIndex: number,
        ) => void,
    },
    WorkCollectionEndDate?: {
        onChangeValue: (
            value: any,
            fieldName: string,
            rowIndex: number,
        ) => void,
    },
    exclude?: {
        onChangeValue: (
            value: any,
            fieldName: string,
            rowIndex: number,
        ) => void,
    },
    order?: {
        onChangeValue: (
            value: any,
            fieldName: string,
            rowIndex: number,
        ) => void,
    },
}

export interface IRowActions {
    iconButton?: {
        icon: FluentIcon,
        renderTitleAsLink?: boolean,
        onClick: (item: IProductWorkDataItems | IInstrumentation | IArtist | IContributorItem | ITitle | IUnpaidClaimsSearchResult | IAgreementWorkDataItems) => void,
    },
    textButton?: {
        text: string,
        onClick: (
            item: IProductWorkDataItems | IInstrumentation | IArtist | IContributorItem | ITitle | IUnpaidClaimsSearchResult | IAgreementWorkDataItems
        ) => void
    },
    
}

const DisplayTable: React.FC<IDisplayTableProps> = ({
    tableContents,
    dataGridTableData,
    isExpandable,
    showRowNumber,
    rowActions,
    removeRowAction,
    lookupOptionsAndOnChangeFunctions,
    isSearchResultTable,
    sortTableByColumn,
    activeSortHeaderSection,
    isAscending,
    isRenderLinkTitle,
    getAgreementDetails,
    isPaginationView,
    addToPackages,
    addCopiedProductWorks,
    showYesNoPrompt,
    hideModal,
    packageActionsDisabled = false,
    packageMessageData,
    deferredPackageSelection = false
}) => {

    const [internalSortKey, setInternalSortKey] = React.useState<string>("");
    const [internalAscending, setInternalAscending] = React.useState<boolean>(false);

    // Used to decide whether to use external sorting function passed as props
    // Or DisplayTable's internal sorting functionality
    const useExternalSorting =
        typeof sortTableByColumn === "function" &&
        typeof activeSortHeaderSection === "string" &&
        typeof isAscending === "boolean";

    const effectiveSortKey = useExternalSorting ? activeSortHeaderSection! : internalSortKey;
    const effectiveAscending = useExternalSorting ? isAscending! : internalAscending;

    const handleSort = React.useCallback(
        (section: string) => {
            if (useExternalSorting) {
                sortTableByColumn!(section, effectiveAscending);
                return;
            }

            const isSameColumn = effectiveSortKey === section;
            const sortAscending = isSameColumn ? effectiveAscending : false; 
            const nextAscending = !sortAscending;

            setInternalSortKey(section);
            setInternalAscending(nextAscending);
        },
        [useExternalSorting, sortTableByColumn, effectiveAscending, effectiveSortKey]
    );

    const sortedTableContents = React.useMemo(() => {
        const rows = Array.isArray(tableContents) ? [...tableContents] : [];

        if (isSearchResultTable) {
            return rows;
        }

        if (!effectiveSortKey) return rows;

        const key = effectiveSortKey;
        const asc = effectiveAscending;

        rows.sort((a: any, b: any) => {
            const getVal = (x: any) => {
                const v = x?.[key];
                const raw = v?.value ?? v ?? "";
                return raw.toString();
            };

            const A = getVal(a);
            const B = getVal(b);

            const cmp = A.localeCompare(B, undefined, { numeric: true });
            return asc ? cmp : -cmp;
        });

        return rows;
    }, [tableContents, effectiveSortKey, effectiveAscending,isSearchResultTable]);


    const [resultsPerPage, setResultsPerPage] = React.useState<number>(10);
    const [currentPage, setCurrentPage] = React.useState<number>(1);
    const [indexOfFirstResult, setIndexOfFirstResult] = React.useState<number>(0);
    const [indexOfLastResult, setIndexOfLastResult] = React.useState<number>(0);
    const filteredHeaderSections = dataGridTableData.filter((section: IRepertoireField) =>
        section.name !== 'noSearchResultsMessage' &&
        !PACKAGE_LOCALIZATION_FIELDS.has(section.name?.toString())
    );

    React.useEffect(() => {
        const len = sortedTableContents?.length ?? 0;
        const per = resultsPerPage ?? 10;
        setCurrentPage(1);
        setIndexOfFirstResult(0);
        setIndexOfLastResult(Math.min(per, len));
    }, [sortedTableContents, resultsPerPage]);

    const updateContentsTable = React.useCallback((
        first: number,
        last: number,
        perPage: number,
        page: number
    ) => {
        setIndexOfFirstResult(first);
        setIndexOfLastResult(last);
        setResultsPerPage(perPage);
        setCurrentPage(page);
    }, []);

    const renderTableContents = () => {
        const rows: JSX.Element[] = [];
        const contentsToRender = isPaginationView
            ? sortedTableContents?.slice(indexOfFirstResult, indexOfLastResult)
            : sortedTableContents;
        let logicalRowNumber = 0;

        contentsToRender?.map((tableContentItem, index: number) => {
            const isDependentRow = (tableContentItem as IContributorItem)?.source?.value === "Agreement";
            const displayRowNumber = isDependentRow ? null : ++logicalRowNumber;
                rows.push(
                    <DisplayTableRow
                    tableContentItem={tableContentItem} 
                    index={index} 
                    tableData={filteredHeaderSections}
                    searchResultsTableData={
                        packageMessageData ?? dataGridTableData
                    }
                    packageActionsDisabled={packageActionsDisabled}
                    deferredPackageSelection={deferredPackageSelection}
                    showRowNumber={showRowNumber} 
                    isExpandable={isExpandable}
                    rowActions={rowActions}
                    removeRowAction={removeRowAction}
                    lookupOptionsAndOnChangeFunctions={lookupOptionsAndOnChangeFunctions}
                    isSearchResultTable={isSearchResultTable}
                    isRenderLinkTitle={isRenderLinkTitle}
                    getAgreementDetails={getAgreementDetails}
                    addToPackages={addToPackages}
                    addCopiedProductWorks={addCopiedProductWorks}
                    isDependentRow={isDependentRow} 
                    displayRowNumber={displayRowNumber}
                    />
                 );
            });   
        return rows;
    };


    const getHeaderSections = () => {


        const headerSections = filteredHeaderSections
            .map((section: IRepertoireField, index) => {

                const totalPerfPercentage = hasValue((sortedTableContents ?? []).map(item => item?.perfPercentage?.value), -1) ? null
                    : Number((sortedTableContents ?? []).reduce((sum: number, item: any) => sum + (Number(item?.perfPercentage?.value ?? 0)), 0)
                ).toFixed(2);

                const totalMechPercentage = hasValue((sortedTableContents ?? []).map(item => item?.mechPercentage?.value), -1) ? null
                    : Number((sortedTableContents ?? []).reduce((sum: number, item: any) => sum + (Number(item?.mechPercentage?.value ?? 0)), 0)
                ).toFixed(2);

            const getSectionData = (section: any) => {
                if (section.componentInstance === CONTRIBUTORS_KEY) {
                    switch (section.name) {
                        case 'perfPercentage':
                            return (
                                <>
                                    <div className="tableHeaderSectionContainer">
                                        {section.data}
                                        <div className="tableHeaderSectionValue">{totalPerfPercentage ?? "-"}</div>
                                    </div>
                                </>
                            );
                        case 'mechPercentage':
                            return (
                                <>
                                    <div className="tableHeaderSectionContainer">
                                        {section.data}
                                        <div className="tableHeaderSectionValue">{totalMechPercentage ?? "-"}</div>
                                    </div>
                                </>
                            );
                        default:
                            return section.data;
                    }
                }

                if (section.componentInstance === SEARCH_VIEW_REGISTERED_WORKS && section.name === ADD_TO_PACKAGE_ACTION) {

                    const entityIds: number[] = (tableContents ?? []).map((item: any) => item?.workID).filter((id: number) => typeof id === "number");

                    return (
                        <div className="tableHeaderSectionContainer">
                            {section.data}
                            <AddAllToPackageButton
                                entityIds={entityIds}
                                addToPackages={addToPackages}
                                searchResultsTableData={packageMessageData ?? dataGridTableData}
                                entityType={EntityType.Work}
                                showYesNoPrompt={showYesNoPrompt}
                                hideModal={hideModal}
                                disabled={packageActionsDisabled}
                                componentInstance={section.componentInstance}
                                deferredSelection={deferredPackageSelection}
                            />
                        </div>
                    );
                }
                return section.data;
            };

            return (
                <td key={`${section.name} ${index}`} className="td">
                    <TableHeaderSection
                        section={getSectionData(section)}
                        fieldName={section.name}
                        isSortingActive={section.name === effectiveSortKey}
                        ascending={effectiveAscending}
                        onClickHeaderSection={(fieldName: string) => handleSort(fieldName)}
                        sortable
                    />
                </td>
            );
        });


        if (showRowNumber) {
            headerSections.splice(0, 0, <td key="rowNumber" title="Row Number"></td>);
        }
        return headerSections;
    };

    const renderHeader = () => {
        let sections = getHeaderSections();
        return <tr key="headerRow" className="tr">{isExpandable && <td />}{sections}</tr>;
    };

    return  dataGridTableData && dataGridTableData.length > 0 ? <div className="tableContainer">
                <table className="table">
                    <thead key="thead" className={"tableContainer table thead tableHeader"}>
                        {renderHeader()}
                    </thead>
                    <tbody className="tbody">
                        {renderTableContents()}
                    </tbody>
                </table>
                {isPaginationView && (
                    <PaginationView
                        contentsLength={tableContents ? tableContents.length : 0}
                        resultsPerPage={resultsPerPage}
                        currentPage={currentPage}
                        updateContentsTable={updateContentsTable}
                    />
                )}
            </div> : <DisplayText text={"Loading ..."} />
}

export default DisplayTable;